<script setup>
/*
    Display a button input widget.

    Components:
        primary: <v-btn>

    Widget Properties:
        prefix
        suffix
        text

    CSS Properties:
        background
        color
        border-*
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2); # drop shadow
        ...
    
    Custom CSS Properties
        --w-variant | Set to elevated, flat, tonal, outlined, text or plain.
        --w-block | Set to true to make the button full width.
        --w-density | Set to compact, default or comfortable.
        --w-ripple | Set to enable the ripple effect.

    Examples:
        prefix: $robot
        text: Click Me
 */

import {reactive, ref} from 'vue'
import {getWidgetProps} from '@/paks/vu-dash/BoardUtils'

const props = defineProps({
    saving: Boolean,
    widget: Object,
})

const page = reactive({
    args: {},
    data: {},
    icon: false,
    loading: false,
})

defineExpose({page, update})

const primary = ref(null)
// These are virtual elements that pass widget.prefix & suffix 
const prefix = ref(null)
const suffix = ref(null)

const DefaultStyles = {
    prefix: {},
    primary: {
        'background': 'rgb(var(--v-theme-primary))',
        'color': 'rgb(var(--v-theme-on-primary))',
        'font-size': 'max(10px, min(min(9cqw, 20cqh), 144px))',
        'font-weight': 'bold',
    },
    suffix: {},
}

async function update() {
    let widget = props.widget
    Object.assign(page, getWidgetProps(widget, 
        {primary, prefix, suffix},
        {styles: DefaultStyles, args: {variant: 'flat'}}
    ))
    let {args, data, styles} = page
    
    if (args.variant == 'outlined' || args.variant == 'text') {
        // Remove button color
        primary.value.$el.style.background = ''
    }
    // Need to map color to the v-btn color arg
    if (styles.color && !args.color) {
        args.color = styles.background
    }
    args['prepend-icon'] = data.prefix ? data.prefix : args.prefix
    args['append-icon'] = data.suffix ? data.suffix : args.suffix
    page.args = args
    page.data = data
}
</script>

<template>
    <v-btn ref="primary" class="button-widget" v-bind="page.args" :loading="page.saving">{{ page.data.primary }}</v-btn>
</template>

<style lang="scss" scoped>
.button-widget {
    container-type: size;
    position: relative;
    height: 100cqh !important;
    width: 100cqw !important;
    text-align: center;
    top: 50cqh;
    transform: translateY(-50%);
    .v-btn__content {
        .v-icon--size-default {
            font-size: inherit !important;
        }
        i.v-icon {
            font-size: inherit !important;
        }
    }
}
</style>
